import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import "./Auth.css";

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/account";
  const isFromCheckout = Boolean(from && from.includes("checkout"));

  const [email, setEmail] = useState(() => localStorage.getItem("tl_remember_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem("tl_remember_email")));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  if (isAuthenticated) {
    return (
      <div className="auth-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      if (rememberMe) {
        localStorage.setItem("tl_remember_email", email.trim());
      } else {
        localStorage.removeItem("tl_remember_email");
      }
      addToast(t("auth.loginSuccess"), "success");
      navigate(from, { replace: true });
    } catch (err) {
      addToast(err.message || t("auth.loginError"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand-badge">
          <Sparkles size={22} />
        </div>

        <p className="section-label">{t("auth.welcomeBack")}</p>
        <h1>{t("auth.signIn")}</h1>
        <p className="auth-card__sub">{t("auth.signInSub")}</p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Email Address */}
          <div className="auth-field">
            <label htmlFor="email">{t("auth.email")}</label>
            <div className="auth-input-wrapper auth-input-wrapper--ltr">
              <span className="auth-input-icon">
                <Mail size={16} />
              </span>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                dir="ltr"
              />
            </div>
          </div>

          {/* Password */}
          <div className="auth-field">
            <div className="auth-field-row">
              <label htmlFor="password">{t("auth.password")}</label>
              <Link to="/forgot-password" className="auth-forgot-link">
                {t("auth.forgotPassword")}
              </Link>
            </div>
            <div className="auth-input-wrapper auth-input-wrapper--ltr">
              <span className="auth-input-icon">
                <Lock size={16} />
              </span>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="has-toggle"
                autoComplete="current-password"
                placeholder={t("auth.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                dir="ltr"
              />
              <button
                type="button"
                tabIndex={-1}
                className="auth-toggle-pwd"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword((prev) => !prev);
                  // Ensure input keeps focus
                  document.getElementById("password")?.focus();
                }}
                title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Remember Me Toggle */}
          <div className="auth-remember-row">
            <label className="auth-remember-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>{t("auth.rememberMe")}</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2
                  size={16}
                  className="spinner"
                  style={{ animation: "spin 1.5s linear infinite" }}
                />
                <span>{t("auth.signingIn")}</span>
              </>
            ) : (
              <>
                <Lock size={15} />
                <span>{t("auth.signIn")}</span>
                <ArrowRight size={14} className="rtl-flip" />
              </>
            )}
          </button>
        </form>

        {/* Guest Checkout Callout (when navigating from Cart or Checkout) */}
        {isFromCheckout && (
          <div className="auth-guest-callout">
            <p>{t("auth.checkoutNotice")}</p>
            <Link to="/guest-checkout" className="auth-guest-btn">
              <span>{t("auth.continueAsGuest")}</span>
              <ArrowRight size={13} className="rtl-flip" />
            </Link>
          </div>
        )}

        {/* Create Account Link */}
        <p className="auth-footer">
          <span>{t("auth.noAccount")}</span>
          <Link to="/register">{t("auth.createAccount")}</Link>
        </p>

        {/* Security Trust Badge */}
        <div className="auth-security-badge">
          <ShieldCheck size={14} color="var(--gold-dark)" />
          <span>{t("auth.trustSecurity")}</span>
        </div>
      </div>
    </div>
  );
}
